import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import Icon from 'react-native-vector-icons/Feather';
import TonyteBackground from '../components/TonyteBackground';
import { auth, FIRESTORE_COLLECTIONS, firestore, functionsEu, httpsCallable } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { Colors, Radius, Typography } from '../theme';
import type { RootStackParamList, TonyteActivity } from '../types';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import { isActivityCurrentOrUpcoming } from '../utils/activityDates';

type Props = NativeStackScreenProps<RootStackParamList, 'CalendarImports'>;

const FIELD_LABELS: Record<string, string> = {
  coverImage: 'Cover image',
  description: 'Description',
  price: 'Pricing',
  tags: 'Tonyte tags',
};

const LUMA_CALENDAR_URL = /^https:\/\/(?:www\.)?(?:luma\.com\/|api\.luma\.com\/ics\/get\?)/i;

type CalendarIntegration = {
  ownerUid: string;
  partnerId?: string;
  partnerName?: string;
  calendarName?: string;
  calendarUrl?: string;
  feedUrl?: string;
  paused?: boolean;
};

type SyncResult = { operations?: { action: string }[] };

function callableErrorMessage(error: unknown) {
  const message = (error as { message?: string })?.message;
  return message && message !== 'internal' ? message : 'Something went wrong. Please try again.';
}

function formatEventDate(event: TonyteActivity) {
  const date = event.startDate || '';
  const time = event.startTime || '';
  return [date, time].filter(Boolean).join(' · ') || 'Date unavailable';
}

export default function CalendarImportsScreen({ navigation, route }: Props) {
  const uid = auth.currentUser?.uid ?? '';
  const { profile } = useProfile(uid);
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  // Icons on neutral surfaces follow the theme; icons on orange buttons stay white.
  const neutralIcon = theme.isDark ? '#fff' : theme.colors.icon;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';
  // One partner's calendar at a time: a partner only ever their own; an admin
  // only the partner whose venue profile they came from — never a merged view
  // of everyone's imports.
  const targetOwnerUid = isAdmin ? route.params?.ownerUid : uid;
  const [events, setEvents] = useState<TonyteActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [integrations, setIntegrations] = useState<CalendarIntegration[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (!uid || !profile) return;
    if (!targetOwnerUid) {
      setEvents([]);
      setIntegrations([]);
      setLoading(false);
      return;
    }
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const activities = collection(firestore, FIRESTORE_COLLECTIONS.activities);
      // Admin reads of a partner's drafts are allowed for calendar imports
      // only (firestore.rules), so the admin query says so explicitly.
      const importedQuery = targetOwnerUid === uid
        ? query(activities, where('ownerUid', '==', uid), limit(100))
        : query(activities, where('ownerUid', '==', targetOwnerUid), where('externalSource.provider', '==', 'luma'), limit(100));
      const snapshot = await getDocs(importedQuery);
      const imported = snapshot.docs
        .map(item => ({ id: item.id, ...item.data() } as TonyteActivity))
        .filter(event => event.externalSource?.provider === 'luma')
        .sort((a, b) => `${a.startDate || ''}${a.startTime || ''}`.localeCompare(`${b.startDate || ''}${b.startTime || ''}`));
      setEvents(imported);

      const integrationSnapshot = await getDoc(doc(firestore, 'partnerCalendarIntegrations', targetOwnerUid));
      setIntegrations(integrationSnapshot.exists() ? [{ ownerUid: targetOwnerUid, ...integrationSnapshot.data() } as CalendarIntegration] : []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [profile, targetOwnerUid, uid]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const publishedCount = useMemo(
    () => events.filter(event => event.publishStatus === 'published').length,
    [events],
  );
  // Only what still needs a decision: upcoming imports that aren't published
  // (or cancelled). Published ones are edited from the event itself / Partner
  // Calendar, and past ones are done — neither belongs in this review queue.
  const toReview = useMemo(
    () => events.filter(event => event.publishStatus !== 'published'
      && event.status !== 'cancelled'
      && isActivityCurrentOrUpcoming(event)),
    [events],
  );

  // Import/sync act only on the calendar being viewed.
  const importTarget = targetOwnerUid ? integrations[0] ?? { ownerUid: targetOwnerUid } : undefined;
  const syncTargets = integrations.filter(item => !item.paused);

  const openImport = () => {
    setImportUrl(importTarget?.calendarUrl || importTarget?.feedUrl || '');
    setImportVisible(true);
  };

  const syncCalendars = async (targets: CalendarIntegration[]) => {
    await auth.currentUser?.getIdToken(true);
    const sync = httpsCallable<{ ownerUid: string }, SyncResult>(functionsEu, 'syncPartnerCalendarEu');
    let created = 0;
    let updated = 0;
    for (const target of targets) {
      const { data } = await sync({ ownerUid: target.ownerUid });
      for (const op of data.operations ?? []) {
        if (op.action === 'create') created += 1;
        if (op.action === 'update' || op.action === 'enrich' || op.action === 'cancel') updated += 1;
      }
    }
    return { created, updated };
  };

  const handleSync = async () => {
    if (!syncTargets.length) {
      Alert.alert('No calendar connected', 'Import a Luma calendar first.');
      return;
    }
    setSyncing(true);
    try {
      const { created, updated } = await syncCalendars(syncTargets);
      Alert.alert('Calendar synced', created || updated
        ? `${created} new, ${updated} updated.`
        : 'Everything is already up to date.');
      await load(true);
    } catch (error) {
      Alert.alert('Sync failed', callableErrorMessage(error));
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async () => {
    const calendarUrl = importUrl.trim();
    if (!LUMA_CALENDAR_URL.test(calendarUrl)) {
      Alert.alert('Invalid link', 'Enter a public Luma calendar link or Luma calendar ICS URL.');
      return;
    }
    if (!importTarget) return;
    setImporting(true);
    try {
      await auth.currentUser?.getIdToken(true);
      const configure = httpsCallable<{ ownerUid: string; calendarUrl: string }, { success: boolean }>(functionsEu, 'configurePartnerCalendarEu');
      await configure({ ownerUid: importTarget.ownerUid, calendarUrl });
      const { created } = await syncCalendars([{ ...importTarget, calendarUrl }]);
      setImportVisible(false);
      Alert.alert('Calendar imported', `${created} new event${created === 1 ? '' : 's'} imported as private drafts.`);
      await load(true);
    } catch (error) {
      Alert.alert('Import failed', callableErrorMessage(error));
    } finally {
      setImporting(false);
    }
  };

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <View style={styles.header}>
        <TouchableOpacity style={[styles.iconButton, appearance.iconButton]} onPress={() => navigation.goBack()}>
          <Icon name="arrow-left" size={20} color={neutralIcon} />
        </TouchableOpacity>
        <View style={styles.headerCopy}>
          <Text style={[styles.kicker, appearance.kicker]}>{isAdmin ? 'ADMIN REVIEW' : 'CALENDAR IMPORTS'}</Text>
          <Text style={[styles.title, appearance.title]} numberOfLines={1}>{isAdmin && route.params?.venueName ? route.params.venueName : 'Imported Luma events'}</Text>
          <Text style={[styles.subtitle, appearance.subtitle]}>Preview, complete and publish calendar drafts.</Text>
        </View>
      </View>

      {!loading ? (
        <>
          <View style={[styles.summary, appearance.summary]}>
            <Summary value={toReview.length} label="To review" />
            <Summary value={publishedCount} label="Published" />
          </View>
          <View style={styles.calendarActions}>
            <TouchableOpacity
              style={[styles.secondaryButton, appearance.secondaryButton, (!importTarget || importing) && styles.buttonDisabled]}
              onPress={openImport}
              disabled={!importTarget || importing}
            >
              <Icon name="download" size={15} color={neutralIcon} />
              <Text style={[styles.buttonText, appearance.secondaryButtonText]}>Import calendar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryButton, (!syncTargets.length || syncing) && styles.buttonDisabled]}
              onPress={() => void handleSync()}
              disabled={!syncTargets.length || syncing}
            >
              {syncing ? <ActivityIndicator size="small" color="#fff" /> : <Icon name="refresh-cw" size={15} color="#fff" />}
              <Text style={styles.buttonText}>{syncing ? 'Syncing…' : 'Sync calendar'}</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : null}

      {loading ? (
        <View style={styles.loading}><ActivityIndicator color={Colors.sunset} /></View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.list}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => void load(true)} tintColor={Colors.sunset} />}
        >
          {toReview.map(event => {
            const missing = event.missingFields ?? [];
            const published = event.publishStatus === 'published';
            const cover = event.coverPhoto || event.coverImageUrl || event.imageUrl;
            return (
              <View key={event.id} style={[styles.card, appearance.card]}>
                {cover ? <Image source={{ uri: cover }} style={[styles.cover, appearance.cover]} /> : <View style={[styles.cover, appearance.cover, styles.coverEmpty]}><Icon name="image" size={28} color={theme.isDark ? '#75808e' : theme.colors.iconFaint} /></View>}
                <View style={styles.cardBody}>
                  <View style={styles.badgeRow}>
                    <View style={[styles.badge, published ? styles.publishedBadge : styles.draftBadge]}>
                      <Text style={[styles.badgeText, appearance.badgeText]}>{published ? 'Published' : 'Private draft'}</Text>
                    </View>
                    <Text style={[styles.partner, appearance.partner]}>{event.partnerId || 'Calendar import'}</Text>
                  </View>
                  <Text style={[styles.eventTitle, appearance.eventTitle]}>{event.title}</Text>
                  <Text style={[styles.meta, appearance.meta]}>{formatEventDate(event)}</Text>
                  <Text style={[styles.meta, appearance.meta]} numberOfLines={1}>{event.locationName || event.address}</Text>
                  <Text style={[styles.description, appearance.description]} numberOfLines={4}>{event.description || 'No description imported.'}</Text>
                  <View style={styles.detailRow}>
                    <Text style={[styles.detailLabel, appearance.detailLabel]}>Price</Text>
                    <Text style={[styles.detailValue, appearance.detailValue]}>{event.isFree === true ? 'Free' : event.price || 'Unknown'}</Text>
                  </View>
                  <View style={[styles.completeness, appearance.completeness]}>
                    <Text style={[styles.completenessTitle, appearance.completenessTitle]}>{missing.length ? 'Still needed before publishing' : 'Ready for final review'}</Text>
                    {missing.length ? (
                      <View style={styles.missingRow}>
                        {missing.map(field => <View key={field} style={[styles.missingChip, appearance.missingChip]}><Text style={[styles.missingText, appearance.missingText]}>{FIELD_LABELS[field] || field}</Text></View>)}
                      </View>
                    ) : <Text style={[styles.readyText, appearance.readyText]}>All required listing fields are present.</Text>}
                  </View>
                  <View style={styles.actions}>
                    <TouchableOpacity style={[styles.secondaryButton, appearance.secondaryButton]} onPress={() => navigation.navigate('EventDetail', { eventId: event.id, event })}>
                      <Icon name="eye" size={15} color={neutralIcon} />
                      <Text style={[styles.buttonText, appearance.secondaryButtonText]}>Preview</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.primaryButton} onPress={() => navigation.navigate('UploadEvent', { event, mode: 'edit' })}>
                      <Icon name="edit-3" size={15} color="#fff" />
                      <Text style={styles.buttonText}>{published ? 'Edit event' : 'Edit & publish'}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            );
          })}
          {!toReview.length ? (
            <Text style={[styles.empty, appearance.empty]}>
              {!targetOwnerUid
                ? 'Open a partner\u2019s venue profile to review their calendar imports.'
                : events.length
                  ? 'Nothing to review. Every upcoming import is published \u2014 edit those from the event page or the Partner Calendar.'
                  : 'No imported Luma events for this calendar yet.'}
            </Text>
          ) : null}
        </ScrollView>
      )}

      <Modal visible={importVisible} transparent animationType="fade" onRequestClose={() => setImportVisible(false)}>
        <View style={[styles.modalBackdrop, appearance.modalBackdrop]}>
          <View style={[styles.modalCard, appearance.modalCard]}>
            <Text style={[styles.modalTitle, appearance.modalTitle]}>Import calendar</Text>
            <Text style={[styles.modalText, appearance.modalText]}>
              {isAdmin && importTarget?.partnerName
                ? `Luma calendar link for ${importTarget.partnerName}.`
                : 'Paste your public Luma calendar link. Upcoming events are imported as private drafts.'}
            </Text>
            <TextInput
              style={[styles.modalInput, appearance.modalInput]}
              value={importUrl}
              onChangeText={setImportUrl}
              placeholder="https://luma.com/your-calendar"
              placeholderTextColor={theme.isDark ? '#6b7684' : theme.colors.textPlaceholder}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="url"
              editable={!importing}
            />
            <View style={styles.actions}>
              <TouchableOpacity style={[styles.secondaryButton, appearance.secondaryButton]} onPress={() => setImportVisible(false)} disabled={importing}>
                <Text style={[styles.buttonText, appearance.secondaryButtonText]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryButton, importing && styles.buttonDisabled]} onPress={() => void handleImport()} disabled={importing}>
                {importing ? <ActivityIndicator size="small" color="#fff" /> : <Icon name="download" size={15} color="#fff" />}
                <Text style={styles.buttonText}>{importing ? 'Importing…' : 'Import'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </TonyteBackground>
  );
}

function Summary({ value, label }: { value: number; label: string }) {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={styles.summaryItem}>
      <Text style={[styles.summaryValue, appearance.summaryValue]}>{value}</Text>
      <Text style={[styles.summaryLabel, appearance.summaryLabel]}>{label}</Text>
    </View>
  );
}

// Midnight stays the canonical StyleSheet below; Daylight only overrides.
const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  return StyleSheet.create({
    container: daylightStyle(theme, { backgroundColor: c.background }),
    iconButton: daylightStyle(theme, { backgroundColor: c.controlBackground }),
    kicker: daylightStyle(theme, { color: c.filterActiveIcon }),
    title: daylightStyle(theme, { color: c.textPrimary }),
    subtitle: daylightStyle(theme, { color: c.textSecondary }),
    summary: daylightStyle(theme, { backgroundColor: c.cardBackground, ...theme.shadows.card }),
    summaryValue: daylightStyle(theme, { color: c.textPrimary }),
    summaryLabel: daylightStyle(theme, { color: c.textSecondary }),
    card: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: c.cardBorder, ...theme.shadows.card }),
    cover: daylightStyle(theme, { backgroundColor: c.backgroundSecondary }),
    badgeText: daylightStyle(theme, { color: c.textPrimary }),
    partner: daylightStyle(theme, { color: c.textMuted }),
    eventTitle: daylightStyle(theme, { color: c.textPrimary }),
    meta: daylightStyle(theme, { color: c.textSecondary }),
    description: daylightStyle(theme, { color: c.bioText }),
    detailLabel: daylightStyle(theme, { color: c.textSecondary }),
    detailValue: daylightStyle(theme, { color: c.textPrimary }),
    completeness: daylightStyle(theme, { backgroundColor: c.surface }),
    completenessTitle: daylightStyle(theme, { color: c.textPrimary }),
    missingChip: daylightStyle(theme, { backgroundColor: 'rgba(242,125,38,0.12)' }),
    missingText: daylightStyle(theme, { color: '#A84C16' }),
    readyText: daylightStyle(theme, { color: c.success }),
    secondaryButton: daylightStyle(theme, { backgroundColor: c.controlBackground, borderWidth: 1, borderColor: c.border }),
    secondaryButtonText: daylightStyle(theme, { color: c.textPrimary }),
    modalBackdrop: daylightStyle(theme, { backgroundColor: c.overlay }),
    modalCard: daylightStyle(theme, { backgroundColor: c.surfaceElevated, borderColor: c.cardBorder }),
    modalTitle: daylightStyle(theme, { color: c.textPrimary }),
    modalText: daylightStyle(theme, { color: c.textSecondary }),
    modalInput: daylightStyle(theme, { backgroundColor: c.inputBackground, borderColor: c.border, color: c.textPrimary }),
    empty: daylightStyle(theme, { color: c.textSecondary }),
  });
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#050912' },
  header: { paddingTop: 58, paddingHorizontal: 18, paddingBottom: 18, flexDirection: 'row', gap: 12 },
  iconButton: { width: 42, height: 42, borderRadius: 13, backgroundColor: '#151d29', alignItems: 'center', justifyContent: 'center' },
  headerCopy: { flex: 1 }, kicker: { color: Colors.sunset, fontSize: 11, fontWeight: '800', letterSpacing: 1.7 },
  title: { color: '#fff', fontSize: 25, fontWeight: '800', marginTop: 3 }, subtitle: { color: '#97a1af', fontSize: 13, marginTop: 4 },
  summary: { flexDirection: 'row', marginHorizontal: 18, marginBottom: 12, borderRadius: Radius.lg, backgroundColor: '#101722', paddingVertical: 12 },
  summaryItem: { flex: 1, alignItems: 'center' }, summaryValue: { color: '#fff', fontSize: 18, fontWeight: '800' }, summaryLabel: { color: '#8994a3', fontSize: 10, marginTop: 2 },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center' }, list: { padding: 18, paddingBottom: 60, gap: 16 },
  card: { overflow: 'hidden', borderRadius: 20, backgroundColor: '#101722', borderWidth: 1, borderColor: '#263040' },
  cover: { width: '100%', height: 180, backgroundColor: '#17202c' }, coverEmpty: { alignItems: 'center', justifyContent: 'center' }, cardBody: { padding: 16 },
  badgeRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }, badge: { borderRadius: 999, paddingHorizontal: 9, paddingVertical: 5 }, draftBadge: { backgroundColor: 'rgba(255,154,60,0.18)' }, publishedBadge: { backgroundColor: 'rgba(44,205,148,0.18)' },
  badgeText: { color: '#fff', fontSize: 10, fontWeight: '800' }, partner: { color: '#768293', fontSize: 10 },
  eventTitle: { color: '#fff', fontSize: 19, fontWeight: '800', marginTop: 12 }, meta: { color: '#aab3bf', fontSize: 12, marginTop: 5 },
  description: { color: '#c7ced7', fontSize: 13, lineHeight: 19, marginTop: 12 }, detailRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 }, detailLabel: { color: '#85909f', fontSize: 12 }, detailValue: { color: '#fff', fontSize: 12, fontWeight: '700', textTransform: 'capitalize' },
  completeness: { marginTop: 14, borderRadius: 13, backgroundColor: '#0a1019', padding: 12 }, completenessTitle: { color: '#fff', fontSize: 12, fontWeight: '700' }, missingRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 9 }, missingChip: { backgroundColor: 'rgba(255,122,0,0.15)', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 5 }, missingText: { color: '#ffad66', fontSize: 10, fontWeight: '700' }, readyText: { color: '#58d6a6', fontSize: 11, marginTop: 7 },
  actions: { flexDirection: 'row', gap: 9, marginTop: 15 }, primaryButton: { flex: 1.25, minHeight: 44, borderRadius: 12, backgroundColor: Colors.sunset, flexDirection: 'row', gap: 7, alignItems: 'center', justifyContent: 'center' }, secondaryButton: { flex: 1, minHeight: 44, borderRadius: 12, backgroundColor: '#283344', flexDirection: 'row', gap: 7, alignItems: 'center', justifyContent: 'center' }, buttonText: { color: '#fff', fontSize: 12, fontWeight: '800' },
  calendarActions: { flexDirection: 'row', gap: 9, marginHorizontal: 18, marginBottom: 4 }, buttonDisabled: { opacity: 0.5 },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', padding: 24 },
  modalCard: { borderRadius: 20, backgroundColor: '#101722', borderWidth: 1, borderColor: '#263040', padding: 18 },
  modalTitle: { color: '#fff', fontSize: 18, fontWeight: '800' }, modalText: { color: '#aab3bf', fontSize: 13, lineHeight: 19, marginTop: 6 },
  modalInput: { marginTop: 14, borderRadius: 12, borderWidth: 1, borderColor: '#263040', backgroundColor: '#0a1019', color: '#fff', fontSize: 14, paddingHorizontal: 12, paddingVertical: 11 },
  empty: { color: '#8d98a7', textAlign: 'center', marginTop: 50, fontSize: Typography.sm },
});
